import { appdata } from './internalModules'
import { Dist } from './Dist'
import * as THREE from "three"
import Graphics from './Graphics'
import { mapMultiply, mapMerge, mapSum,} from './utility'
import {WallType} from './internalModules'
import { ALayer, buckets } from './internalModules'

export const columnHeightRefNames = [
  'By Dimension',
  'To Bottom of Framing',
  'To Top of Framing',
  'To Top of Solid Layers',
]

export class AssemblyHorizontal extends WallType {
  layersColumn: ALayer[]
  layersFraming: ALayer[]
  
  constructor(height: Dist, width: Dist, length: Dist){
    super(height, width)
    this.className = 'AssemblyHorizontal'
    this.length = length
    this.layers = []
    this.layersColumn = []
    this.layersFraming = []
    this.camera = Graphics.getNewPerspectiveCameraHorizAssembly()
  }

  get area(): number {
    return this.width.ft * this.length.ft
  }

  get solidLayersThickness(): Dist {
    let t = 0
    for (const layer of this.layers){
      t += layer.totalThickness.ft
    }
    return Dist.feet(t)
  }

  get framingThickness(): Dist {
    let t = 0
    for (const framing of this.layersFraming){
      const member = appdata.getMember(framing.memberGuid)
      if(member.depth.ft > t){
        t = member.depth.ft
      }
    }
    return Dist.feet(t)
  }

  static empty = new AssemblyHorizontal(Dist.zero, Dist.zero, Dist.zero)

  updateLength(newLength: Dist) {
    this.length = newLength
    this.calculateGwpSF()
    this.regenGeometry()
  }

  /**update a layer in the specified bucket */
  updateLayer(bucket: string, layer: ALayer, index: number){
    switch (bucket) {
      case 'HorizontalSolid':
        this.layers.splice(index, 1, layer)
        break
      case 'HorizontalFraming':
        this.layersFraming.splice(index, 1, layer)
        break
      case 'Column':
        this.layersColumn.splice(index, 1, layer)
        break
      default:
        break
    }
    this.calculateGwpSF()
    this.regenGeometry()
  }

  //method to reorder a layer
  reorderLayer(bucket: string, fromIndex: number, toIndex: number) {
    let layerArray = this.layers
    if (bucket === buckets.hFraming){
      layerArray = this.layersFraming
    }
    let layer = layerArray[fromIndex]
    layerArray.splice(fromIndex, 1)
    layerArray.splice(toIndex, 0, layer)
    this.regenGeometry()
  }

  /**returns the array of layers of the specified bucket */
  getLayers(layerbucket: string): ALayer[] {
    let layers: ALayer[] = []
    switch (layerbucket) {
      case buckets.hSolid:
        layers = this.layers
        break
      case buckets.hFraming:
        layers = this.layersFraming
        break
      case buckets.column:
        layers = this.layersColumn
        break
      default:
        break
    }
    return layers
  }

  /**returns all layers */
  getAllLayers(): ALayer[] {
    return this.getLayers(buckets.column).concat(this.getLayers(buckets.hFraming), this.getLayers(buckets.hSolid))
  }

  /**add a layer to the specified layer bucket */
  addLayer(layerbucket: string, layer: ALayer){
    switch (layerbucket) {
      case buckets.hSolid:
        this.layers.push(layer)
        break
      case buckets.hFraming:
        this.layersFraming.push(layer)
        break
      case buckets.column:
        this.layersColumn.push(layer)
        break
      default:
        break
    }
    
    this.calculateGwpSF()
    this.regenGeometry()
  }

  removeLayer(layerbucket: string, index: number){
    if (layerbucket === buckets.hSolid){
      this.layers.splice(index, 1)
    }
    if (layerbucket === buckets.hFraming){
      this.layersFraming.splice(index, 1)
    }
    if (layerbucket === buckets.column){
      this.layersColumn.splice(index, 1)
    }
    this.calculateGwpSF()
    this.regenGeometry()
  }

  //method to calculate GWP per SF
  calculateGwpSF(){
    this.gwpsfByMaterial.clear()
    //calc solid layers
    for (const layer of this.layers) {
      //add the material-gwp key-values for each layer
      mapMerge(this.gwpsfByMaterial, layer.getMaterialGwpMap(this))
    }
    //calc framing layers
    for (const layer of this.layersFraming){
      mapMerge(this.gwpsfByMaterial, layer.getMaterialGwpMap(this))
    }
    // calc columns
    for (const layer of this.layersColumn){
      const columnMap = layer.getMaterialGwpMap(this)
      mapMerge(this.gwpsfByMaterial, columnMap)
    }
    //store total gwp per sf from all materials
    this.gwpsf = mapSum(this.gwpsfByMaterial) / this.area
    //divide each gwp in the map by the area
    mapMultiply(this.gwpsfByMaterial, 1 / this.area)
  }

  getColumnHeight(layer: ALayer): Dist {
    const index = this.layersColumn.findIndex( item => Object.is(item, layer))
    let height = Dist.zero
    const columnHeight = this.getColumnHeights()[index]
    if (columnHeight){
      height = columnHeight
    }
    if( height.ft < 0){
      height = Dist.zero
    }
    return height
  }

  getColumnHeights(): Dist[] {
    let heights: Dist[] = []
    let bot = 0    
    let top = 0
    for(const column of this.layersColumn){
      //console.log({stage:'start loop', i, top, bot})
      bot = top // top is the new bottom
      switch (column.columnHeightRef) {
        case 0: // by custom height
          top += column.columnHeightDim.ft
          if (top > this.height.ft || top === this.height.ft){
            //console.log(`module height reached`)
            top = this.height.ft
          }
          break
        case 1: // to Bottom of Framing
          top = this.height.ft - this.solidLayersThickness.ft - this.framingThickness.ft
          break
        case 2: // to Top of Framing
          top = this.height.ft - this.solidLayersThickness.ft
          break
        case 3: // to Top of Module
          top = this.height.ft
          break
        default:
          break
      }
      //console.log({stage:'end loop', i, top, bot})
      heights.push(Dist.feet(top - bot))
    }
    return heights
  }

  //method to return geometry of each layer and columns
  getMesh(startingOffset = 0, explodeAmount: number): [THREE.Mesh, number] {
    let parentMesh = new THREE.Mesh()
    let yOffset = startingOffset
    // get columns
    let col_yOffset = yOffset
    for (const layer of this.layersColumn){
      const column = layer.getMesh(this)
      column.translateX(-this.width.ft/2)
      column.translateZ(-this.length.ft/2)
      column.translateY(col_yOffset)
      parentMesh.add(column)
      const positions = [[0,1],[1,1],[1,0]]
      for (const pos of positions) {
        const columnCopy = column.clone()
        columnCopy.translateX(this.width.ft * pos[0])
        columnCopy.translateZ(this.length.ft * pos[1])
        parentMesh.add(columnCopy)
      }
      col_yOffset += this.getColumnHeight(layer).ft
    }
    yOffset += explodeAmount + this.height.ft - this.solidLayersThickness.ft
    // get framing layers
    for (const layer of this.layersFraming){
      const mesh = layer.getMesh(this)
      mesh.translateY(yOffset)
      parentMesh.add(mesh)
      yOffset += explodeAmount
    }
    // get solid layers
    for (const layer of this.layers) {
      const mesh = layer.getMesh(this)
      mesh.rotateX(Math.PI / 2)
      mesh.position.y = yOffset + (layer.totalThickness.ft / 2)
      parentMesh.add(mesh)
      yOffset += explodeAmount + layer.totalThickness.ft
    }
    return [parentMesh, yOffset]
  }

  //custom method to convert to json
  toJSON(): object {
    return {
      className: this.className,
      guid: this.guid,
      name: this.name,
      height: this.height.ft.toString(),
      width: this.width.ft.toString(),
      length: this.length.ft.toString(),
      layers: this.layers,
      layersGlazed: [],
      layersFraming: this.layersFraming,
      layersColumn: this.layersColumn,
      wwr: '',
    }
  }


}